/**
 * Tuiles du tableau de bord des espaces M&R (lot 7, 2026-10-29) : boîte de réception, automatisations, calendrier.
 * Un espace M&R (origine CRM) n'a pas de pipeline de prospection : son accueil parle de conversations à répondre,
 * d'automatisations actives et de publications. Mêmes clés react-query que les pages correspondantes.
 */
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CalendarClock, MessageSquare, Zap } from 'lucide-react';
import { apiRequest } from '@/lib/api';
import { useFeatures } from '@/hooks/useFeatures';
import { useInbox } from '@/hooks/useInbox';
import { useAutomationsAccess } from '@/lib/automations';

function Tile({ tile, kicker, icon, tint, onClick, children }: { tile: string; kicker: string; icon: ReactNode; tint: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} data-tile={tile} className="card group relative h-full w-full overflow-hidden p-5 text-left">
      <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-25 blur-2xl" style={{ background: tint }} />
      <div className="flex items-center justify-between">
        <span
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white"
          style={{ background: `linear-gradient(135deg, ${tint}, ${tint}cc)`, boxShadow: `0 8px 18px -8px ${tint}` }}
        >
          {icon}
        </span>
        <ArrowRight size={12} className="text-ink/25 transition group-hover:translate-x-0.5 group-hover:text-pink" />
      </div>
      <p className="mt-4 font-mono text-[9.5px] font-700 uppercase tracking-[0.14em] text-ink/45">{kicker}</p>
      {children}
    </button>
  );
}

/** Conversations à répondre (et en retard), vers la boîte de réception. */
export function InboxTile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useInbox('to_answer', null);
  const rows = data?.conversations ?? [];
  const late = rows.filter((r) => r.overdue).length;
  return (
    <Tile tile="inbox" kicker={t('dashboard.mr.inbox')} icon={<MessageSquare size={15} />} tint="#06b6d4" onClick={() => navigate('/conversations')}>
      <p className="mt-1 font-display text-[40px] font-800 leading-none tracking-[-0.03em]">{rows.length}</p>
      <p className="mt-1.5 text-[12.5px] font-500 text-ink/55">
        {t('dashboard.mr.toAnswer')}
        {late > 0 && <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-700 text-amber-700">{late} {t('dashboard.mr.overdue')}</span>}
        {rows.length === 0 && <span className="ml-2 text-ink/40">· {t('dashboard.mr.allAnswered')}</span>}
      </p>
    </Tile>
  );
}

/** Automatisations actives et activité des sept derniers jours, vers la liste. */
export function AutomationsTile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { automations_enabled: automationsEnabled } = useFeatures();
  const access = useAutomationsAccess(automationsEnabled === true);
  const enabled = automationsEnabled === true && access.data?.enabled === true;
  const { data } = useQuery({
    queryKey: ['automations'],
    queryFn: () => apiRequest<{ automations: { status: string; week: { triggered: number; callback_requested: number } }[] }>('/automations'),
    staleTime: 30_000,
    enabled,
  });
  const list = data?.automations ?? [];
  const active = list.filter((a) => a.status === 'active').length;
  const triggered = list.reduce((n, a) => n + (a.week?.triggered ?? 0), 0);
  const callbacks = list.reduce((n, a) => n + (a.week?.callback_requested ?? 0), 0);
  return (
    <Tile tile="automations" kicker={t('dashboard.mr.automations')} icon={<Zap size={15} />} tint="#8b5cf6" onClick={() => navigate(enabled ? '/automatisations' : '/comptes')}>
      {enabled ? (
        <>
          <p className="mt-1 font-display text-[40px] font-800 leading-none tracking-[-0.03em]">{active}</p>
          <p className="mt-1.5 text-[12.5px] font-500 text-ink/55">{t('dashboard.mr.active')}</p>
          <p className="mt-2 text-[12px] text-ink/50">{t('dashboard.mr.week', { triggered, callbacks })}</p>
        </>
      ) : (
        <p className="mt-2 text-[13px] leading-[1.5] text-ink/60">{t('dashboard.mr.automationsSoon')}</p>
      )}
    </Tile>
  );
}

/** Publier et programmer, vers le calendrier (seulement si la publication est ouverte). */
export function CalendarTile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { publishing_enabled: publishingEnabled } = useFeatures();
  if (!publishingEnabled) return null;
  return (
    <Tile tile="calendar" kicker={t('dashboard.mr.calendar')} icon={<CalendarClock size={15} />} tint="#f59e0b" onClick={() => navigate('/calendrier')}>
      <p className="mt-1 font-display text-[17px] font-800 leading-[1.2] tracking-[-0.02em]">{t('dashboard.mr.calendarTitle')}</p>
      <p className="mt-2 text-[12.5px] leading-[1.5] text-ink/60">{t('dashboard.mr.calendarBody')}</p>
    </Tile>
  );
}
